import {RootLayout, RsnButton, RsnDialog, RsnMessage, SkeletonContextProvider, useBoolean} from '@raison/ui-kit';
import React, {useEffect} from 'react';
import {useDispatch} from 'react-redux';
import {useNavigate} from 'react-router';
import {LoadProblemPane} from '~/entities/fallbacks';
import {useAddNewCardIOS} from '~/processes/cards';
import {checkNativeStripeAvailability} from '~/processes/cards/api/stripeNative';
import {cardsToView} from '~/processes/cards/ui/list/CardsContainer/cardsToView';
import {operationsRequestRoutine} from '~/redux/finances/financesEffects';
import {addNewCard} from '~/redux/payments/paymentActions';
import {useAppDispatch} from '~/shared/store';
import {useCardsQuery} from '../../../api/cardQueryHooks';
import {CardListItem} from './CardListItem';
import {PaymentRestrictionsModal} from './PaymentRestrictionsModal';

export type CardsContainerProps = {
    isPaymentRestricted: boolean;
    isCardsDisabled: boolean;
};

function useEnsureFinancesData() {
    const dispatch = useDispatch();
    useEffect(() => {
        dispatch(operationsRequestRoutine.trigger());
    }, [dispatch]);
}

export function CardsPage({isPaymentRestricted, isCardsDisabled}: CardsContainerProps) {
    const {data: cards, isSuccess, isError, isRefetching, refetch} = useCardsQuery();
    useEnsureFinancesData();
    const navigate = useNavigate();
    const dispatch = useAppDispatch();
    const [isRestrictedOpen, setIsRestrictedOpen] = useBoolean(false);

    const onCardAdded = () => {
        refetch();
        dispatch(operationsRequestRoutine.trigger());
    };

    const {addNewCard: addNewCardViaNativeDialog, isPending} = useAddNewCardIOS({
        onSuccess: onCardAdded,
    });

    const handleAddNewCard = () => {
        if (checkNativeStripeAvailability()) {
            addNewCardViaNativeDialog();
            return;
        }
        dispatch(addNewCard);
        navigate('/cards/add');
    };

    const canAddNewCard = !isCardsDisabled && !isPending;

    function renderList() {
        if (isRefetching || !isSuccess) {
            return <CardsListSkeleton isError={isError} />;
        }
        if (!cards.length) {
            return <EmptyCardsList />;
        }
        return cardsToView(cards, isCardsDisabled).map(cardView => <CardListItem key={cardView.id} {...cardView} />);
    }

    return (
        <RootLayout title='My cards'>
            {isPaymentRestricted && (
                <div className={'p-4'}>
                    <RsnMessage.Notification arrowed onClick={setIsRestrictedOpen.on}>
                        This type of operation isn’t available for citizens and residents from some countries.
                    </RsnMessage.Notification>
                </div>
            )}
            <div className={'flex flex-1 flex-col'}>{renderList()}</div>
            <div className={'p-4'}>
                <RsnButton.Primary disabled={!canAddNewCard} onClick={handleAddNewCard}>
                    Add new card
                </RsnButton.Primary>
            </div>
            <RsnDialog.Controller isOpen={isRestrictedOpen} onClose={setIsRestrictedOpen.off}>
                <PaymentRestrictionsModal />
            </RsnDialog.Controller>
        </RootLayout>
    );
}

function CardsListSkeleton({isError}: {isError: boolean}) {
    return (
        <SkeletonContextProvider value={{stopped: isError}}>
            <div className={'relative w-full'}>
                <CardListItem.Skeleton />
                <CardListItem.Skeleton />
                <CardListItem.Skeleton />
                <CardListItem.Skeleton />
                <CardListItem.Skeleton />
                <CardListItem.Skeleton />
                <div className={'absolute top-0 z-10 h-full w-full bg-gradient-to-b from-transparent to-neutral-10'} />
            </div>
            {isError && <LoadProblemPane reset={undefined} />}
        </SkeletonContextProvider>
    );
}

function EmptyCardsList() {
    return (
        <div className={'flex flex-1 items-center justify-center p-4 text-center text-neutral-60'}>
            You haven’t added any cards yet
        </div>
    );
}
